/**
 * Wires "Book Now" CTAs (.js-action[data-action="open-calendar"]) to the
 * MediSpa calendar. Extracted from the legacy jQuery js-action dispatcher in
 * assets/custom.js so jQuery-free pages (the sanitized MediSpa landing pages)
 * can open the calendar without loading custom.js.
 *
 * Requires:
 * - window.theme.medispaCalendarConfig (rendered by the medispa-calendar-config
 *   or medispa-calendar-config-inline snippet)
 * - the .medispa-calendar--placeholder element + React calendar bundle
 *   (rendered by the medispa-calendar-placeholder snippet)
 */
import type { CalendarProduct } from '@/@types/calendar';

function findProductsFragment(startEl: Element): HTMLScriptElement | null {
  let currentEl: Element | null = startEl;

  while (currentEl && currentEl !== document.documentElement) {
    const fragment =
      currentEl.querySelector<HTMLScriptElement>('script[data-medispa-products-fragment]') ||
      currentEl.querySelector<HTMLScriptElement>("script[id^='medispa-products-fragment-']");

    if (fragment) return fragment;

    currentEl = currentEl.parentElement;
  }

  return null;
}

function dispatchCalendarConfig(): void {
  document.dispatchEvent(
    new CustomEvent('theme:medispaCalendarConfig', {
      detail: window.theme.medispaCalendarConfig,
    }),
  );
}

function openCalendar(event: MouseEvent, actionEl: Element): void {
  event.preventDefault();
  event.stopPropagation();

  const config = window.theme?.medispaCalendarConfig;

  if (!config) {
    console.error(
      'medispa-booking-actions: window.theme.medispaCalendarConfig is missing — render the medispa-calendar-config snippet before using open-calendar CTAs',
    );
    return;
  }

  const startEl = event.target instanceof Element ? event.target : actionEl;

  // A products fragment near the CTA overrides the page-level product list
  // (e.g. a dropdown scoped to a specific set of booking products).
  const fragmentEl = findProductsFragment(startEl);

  if (fragmentEl?.textContent) {
    try {
      const products = JSON.parse(fragmentEl.textContent) as CalendarProduct[];

      config.products = products;

      if (products[0]) {
        config.selectedProduct = products[0].id.toString();
        config.depositAmount = products[0].deposit_amount ?? config.depositAmount;
        config.payInStoreAmount = products[0].pay_in_store_amount ?? config.payInStoreAmount;
      }

      dispatchCalendarConfig();
    } catch (error) {
      console.error('medispa-booking-actions: failed to parse products fragment', error);
    }
  }

  const mainCta = startEl.closest('[data-product-id]');
  if (!mainCta) return;

  const productId = mainCta.getAttribute('data-product-id');
  if (!productId) return;

  config.selectedProduct = productId;
  dispatchCalendarConfig();

  document.documentElement.classList.add('lock-scrolling');

  const calendarElem = document.querySelector('.medispa-calendar--placeholder');
  if (!calendarElem) {
    console.error(
      'medispa-booking-actions: .medispa-calendar--placeholder not found — render the medispa-calendar-placeholder snippet',
    );
    return;
  }

  calendarElem.classList.add('active');
}

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;

  const actionEl = event.target.closest('.js-action[data-action="open-calendar"]');
  if (!actionEl) return;

  openCalendar(event, actionEl);
});

// Booking dropdown open/close toggle. On jQuery-based layouts custom.js binds
// this; this entrypoint only loads on the jQuery-free LP layout, so there is
// no double-binding.
document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;

  const trigger = event.target.closest('.medispa-dropdown-trigger');
  if (!trigger) return;

  trigger.classList.toggle('open');
  trigger.closest('.booking-wrapper')?.classList.toggle('open');
});
